import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError } from "../api/client";
import { BADGE_PATTERN, scan, type ScanResult } from "../api/kiosk";
import GearLink from "../components/GearLink";
import { ConnectionPill, ExpiryBanner } from "../components/KioskChrome";
import { CHOICE_DISMISS_MS, DISMISS_MS, ResultCard } from "../components/ResultCard";
import { useDevice } from "../context/DeviceContext";
import { useClock } from "../hooks/useClock";
import { useOnline } from "../hooks/useOnline";
import { useScannerInput } from "../hooks/useScannerInput";
import "./ScanScreen.css";

/**
 * Untouched this long, a choice-mode device leaves this page for the chooser.
 * Same value as FindScreen's IDLE_MS.
 */
const IDLE_MS = 45_000;

type Phase =
  { name: "idle" } | { name: "working" } | { name: "result"; result: ScanResult };

/**
 * What the chooser (ChooseScreen, D-019) hands over when a scanner fires on it:
 * the badge it read, or that it read one too slowly. Router state, consumed
 * once on arrival.
 */
type Forwarded = { badge?: unknown; slowScan?: unknown } | null;

/**
 * The classic scan screen: one bracketed target, "Scan your badge.", and "No
 * badge? Type your name" in the footer. `/scan` always; `/` on a device set to
 * Scan, classic (D-019).
 *
 * Where it rests depends on the device's mode. In choice mode the kiosk's home
 * is the chooser at `/`: every result, once dismissed, and 45 seconds of
 * nothing happening hand the lobby back there. Otherwise this page is home —
 * results clear back to its own idle, and nothing moves it on by itself.
 */
export default function ScanScreen() {
  const navigate = useNavigate();
  const { state, pathname } = useLocation();
  const { device, evict, checkInMode } = useDevice();
  const { online, reportRequest } = useOnline();
  const now = useClock();
  const returnsHome = checkInMode === "choice";

  const [phase, setPhase] = useState<Phase>({ name: "idle" });

  // Guards against an older, slower request painting over a newer scan. The
  // student in front of the screen must always see their own result.
  const scanSeqRef = useRef(0);
  const dismissTimerRef = useRef(0);
  // Held so an ambiguous choice can re-submit the same badge with a batch id.
  const pendingBadgeRef = useRef<string | null>(null);
  // Check-ins still out: the idle timeout never fires under one.
  const inFlightRef = useRef(0);
  const idleTimerRef = useRef(0);
  // False once unmounted, so a response landing after the kiosk has moved on
  // cannot arm a dismiss timer that navigates from wherever it now is. (A flag
  // rather than bumping scanSeqRef: StrictMode's rehearsal unmount would
  // otherwise discard a forwarded scan's answer.)
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = 0;
    }
  }, []);

  useEffect(() => clearDismissTimer, [clearDismissTimer]);

  const showResult = useCallback(
    (result: ScanResult) => {
      if (!mountedRef.current) return;
      clearDismissTimer();
      setPhase({ name: "result", result });
      const ms = result.kind === "ambiguous" ? CHOICE_DISMISS_MS : DISMISS_MS;
      dismissTimerRef.current = window.setTimeout(() => {
        dismissTimerRef.current = 0;
        pendingBadgeRef.current = null;
        if (returnsHome) navigate("/", { replace: true });
        else setPhase({ name: "idle" });
      }, ms);
    },
    [clearDismissTimer, navigate, returnsHome],
  );

  const submit = useCallback(
    async (badge: string, batchId?: number) => {
      const token = device?.kioskToken;
      if (!token) {
        navigate("/setup", { replace: true });
        return;
      }

      const seq = ++scanSeqRef.current;
      pendingBadgeRef.current = badge;
      clearDismissTimer();
      setPhase({ name: "working" });

      inFlightRef.current += 1;
      try {
        const result = await scan(token, badge, batchId);
        if (seq !== scanSeqRef.current) return; // superseded by a newer scan
        reportRequest(result.kind !== "unavailable");
        showResult(result);
      } catch (error) {
        if (seq !== scanSeqRef.current) return;
        // scan() only throws for a rejected device token; everything else comes
        // back as a renderable result.
        if (error instanceof ApiError && error.isInvalidDevice) {
          evict("This device needs to be re-registered.");
          navigate("/setup", { replace: true });
          return;
        }
        reportRequest(false);
        showResult({
          kind: "unavailable",
          message: "Connection problem — try again or see the coach.",
        });
      } finally {
        inFlightRef.current -= 1;
      }
    },
    [clearDismissTimer, device?.kioskToken, evict, navigate, reportRequest, showResult],
  );

  const handleScan = useCallback((badge: string) => void submit(badge), [submit]);

  // A correct code delivered too slowly (typically a Bluetooth-paired scanner
  // on a tablet/phone) — tell the person at the door instead of doing nothing.
  const handleSlowScan = useCallback(
    () =>
      showResult({
        kind: "slow_scan",
        message: "Scan read too slowly — hold steady and scan again.",
      }),
    [showResult],
  );

  const { inputRef } = useScannerInput({
    onScan: handleScan,
    onSlowScan: handleSlowScan,
  });

  // A scan that landed on the chooser, forwarded here (D-019): submit it once,
  // then drop it from the history entry so a reload of /scan can't post it
  // again. The ref holds across StrictMode's second effect run, which comes
  // before the cleared state does.
  const forwardedRef = useRef(false);
  useEffect(() => {
    const forwarded = state as Forwarded;
    if (forwardedRef.current || !forwarded) return;
    if (forwarded.badge === undefined && forwarded.slowScan === undefined) return;
    // Deferred a tick so the one-shot work runs after StrictMode's practice
    // unmount in dev: the dismiss-timer cleanup that unmount triggers would
    // otherwise kill a slow-scan card's timer the moment it was set, and the
    // card would sit there until the idle timer took over. In production the
    // effect runs once and the tick changes nothing.
    const tick = window.setTimeout(() => {
      forwardedRef.current = true;
      navigate(".", { replace: true, state: null });
      if (typeof forwarded.badge === "string" && BADGE_PATTERN.test(forwarded.badge)) {
        // A one-shot handoff, not a sync loop: this runs once per arrival (the
        // ref), and what it starts is a request to the server — the same call
        // a scan on this screen makes from the scanner hook's listener.
        void submit(forwarded.badge);
      } else if (forwarded.slowScan === true) {
        handleSlowScan();
      }
    }, 0);
    return () => window.clearTimeout(tick);
  }, [handleSlowScan, navigate, state, submit]);

  // Choice mode only: 45 s with no scan, key or touch goes back to the chooser.
  // Never under a request or a visible result — the result's own dismiss timer
  // takes the kiosk home, so it always gets its full time. Listening on the
  // document, as the scanner hook does, so a keystroke counts wherever focus is.
  useEffect(() => {
    if (!returnsHome) return;
    const arm = () => {
      window.clearTimeout(idleTimerRef.current);
      const expire = () => {
        if (inFlightRef.current > 0 || dismissTimerRef.current) {
          idleTimerRef.current = window.setTimeout(expire, IDLE_MS);
          return;
        }
        navigate("/", { replace: true });
      };
      idleTimerRef.current = window.setTimeout(expire, IDLE_MS);
    };
    arm();
    document.addEventListener("keydown", arm, true);
    document.addEventListener("pointerdown", arm, true);
    return () => {
      document.removeEventListener("keydown", arm, true);
      document.removeEventListener("pointerdown", arm, true);
      window.clearTimeout(idleTimerRef.current);
    };
  }, [navigate, returnsHome]);

  const handleChoice = useCallback(
    (batchId: number) => {
      const badge = pendingBadgeRef.current;
      if (badge) void submit(badge, batchId);
    },
    [submit],
  );

  return (
    <div className="screen scan">
      <div className="zebra-field" aria-hidden="true" />

      {/*
        The scanner types into this. Visually hidden rather than `display: none`
        so it can actually hold focus.
      */}
      <input
        ref={inputRef}
        className="scanner-sink"
        type="text"
        value=""
        onChange={() => {}}
        aria-hidden="true"
        tabIndex={-1}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
      />

      <header className="topbar">
        <GearLink />
        <span className="scan-branch">{device?.branchName || "Unregistered"}</span>
        <span className="spacer" />
        <ConnectionPill online={online} />
      </header>

      <ExpiryBanner expiresAt={device?.expiresAt} />

      <main className="scan-stage">
        {phase.name === "idle" && <BadgeIdle now={now} />}
        {phase.name === "working" && (
          <p className="working" role="status">
            Checking…
          </p>
        )}
        {phase.name === "result" && (
          <ResultCard result={phase.result} onChoose={handleChoice} />
        )}
      </main>

      <footer className="scan-footer">
        <button type="button" className="btn-ghost" onClick={() => navigate("/today")}>
          Today&rsquo;s classes
        </button>
        {/* Carries this device's home to the find screen, so its Back and its
            results return to the right place: the chooser in choice mode,
            otherwise `/scan` (in Scan, classic mode `/` is this same page). */}
        <button
          type="button"
          className="btn-ghost btn-ghost--student"
          onClick={() =>
            navigate("/find", {
              replace: true,
              // Home is this screen's own address: "/" for the chooser in choice
              // mode, else wherever this scan page is mounted ("/" in scan mode,
              // "/scan" always), so the find screen returns to the same URL.
              state: { home: returnsHome ? "/" : pathname },
            })
          }
        >
          No badge? Type your name
        </button>
        <span className="spacer" />
        <img
          className="brand-lockup"
          src="/brand/logo-horizontal.png"
          alt="Zebra Robotics"
        />
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------------ pieces */

/**
 * The classic resting state: one bracketed target with a sweeping gold line,
 * so it reads as "put the card here" without a word of instruction.
 */
function BadgeIdle({ now }: { now: Date }) {
  return (
    <div className="idle">
      <div className="idle-target" aria-hidden="true">
        <span className="idle-sweep" />
      </div>
      <p className="eyebrow idle-eyebrow">Zebra Robotics · Check-in</p>
      {/* The system's hero pattern: white display type with one word in gold. */}
      <h1 className="idle-prompt display">
        Scan your
        <br />
        <span className="accent">badge.</span>
      </h1>
      {/* As the original wrote it (not clockLine): three text nodes, not one,
          so the line lays out exactly as it did. */}
      <p className="idle-clock">
        {now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
        {"  ·  "}
        {now.toLocaleDateString(undefined, {
          weekday: "long",
          day: "numeric",
          month: "long",
        })}
      </p>
    </div>
  );
}
